import { useState, useEffect } from 'react';
import { timeEntriesAPI, expenseEntriesAPI, projectsAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Clock, Receipt, Trash2, Loader2, FolderKanban } from 'lucide-react';
import { format } from 'date-fns';

export default function TimeExpenses() {
  const { t } = useLanguage();
  const { formatAmount, getCurrencySymbol } = useCurrency();
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addToProjectId, setAddToProjectId] = useState('');
  
  const [timeForm, setTimeForm] = useState({
    project_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    hours: '',
    notes: ''
  });
  
  const [expenseForm, setExpenseForm] = useState({
    project_id: '',
    expense_type: '',
    cost: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });

  const fetchData = async () => {
    try {
      const [timeRes, expenseRes, projectsRes] = await Promise.all([
        timeEntriesAPI.getAll(),
        expenseEntriesAPI.getAll(),
        projectsAPI.getAll()
      ]);
      setTimeEntries(timeRes.data);
      setExpenseEntries(expenseRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetTimeForm = () => {
    setTimeForm({
      project_id: addToProjectId || '',
      date: format(new Date(), 'yyyy-MM-dd'),
      hours: '',
      notes: ''
    });
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      project_id: addToProjectId || '',
      expense_type: '',
      cost: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      notes: ''
    });
  };

  const openTimeDialog = (projectId = '') => {
    setAddToProjectId(projectId);
    setTimeForm({
      project_id: projectId,
      date: format(new Date(), 'yyyy-MM-dd'),
      hours: '',
      notes: ''
    });
    setTimeDialogOpen(true);
  };

  const openExpenseDialog = (projectId = '') => {
    setAddToProjectId(projectId);
    setExpenseForm({
      project_id: projectId,
      expense_type: '',
      cost: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      notes: ''
    });
    setExpenseDialogOpen(true);
  };

  const handleTimeSubmit = async (e) => {
    e.preventDefault();
    if (!timeForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!timeForm.hours || parseFloat(timeForm.hours) <= 0) {
      toast.error('Please enter valid hours');
      return;
    }

    setSaving(true);
    try {
      await timeEntriesAPI.create({
        ...timeForm,
        hours: parseFloat(timeForm.hours)
      });
      toast.success(t('timeEntryAdded'));
      setTimeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!expenseForm.expense_type) {
      toast.error('Please select expense type');
      return;
    }
    if (!expenseForm.cost || parseFloat(expenseForm.cost) <= 0) {
      toast.error('Please enter valid cost');
      return;
    }

    setSaving(true);
    try {
      await expenseEntriesAPI.create({
        ...expenseForm,
        cost: parseFloat(expenseForm.cost)
      });
      toast.success(t('expenseEntryAdded'));
      setExpenseDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTime = async (entry) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await timeEntriesAPI.delete(entry.id);
      toast.success(t('timeEntryDeleted'));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteExpense = async (entry) => {
    if (!window.confirm('Delete this expense entry?')) return;
    try {
      await expenseEntriesAPI.delete(entry.id);
      toast.success(t('expenseEntryDeleted'));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const EXPENSE_TYPES = [
    { value: 'products', label: t('products') },
    { value: 'travel', label: t('travel') },
    { value: 'parking', label: t('parking') },
    { value: 'materials', label: t('materials') },
    { value: 'other', label: t('other') }
  ];

  // Group entries by project
  const getProjectGroups = () => {
    const projectMap = new Map();
    
    // Seed from projects that have time or expense entries
    timeEntries.forEach((entry) => {
      if (!projectMap.has(entry.project_id)) {
        projectMap.set(entry.project_id, {
          id: entry.project_id,
          name: entry.project_name,
          client_name: entry.client_name,
          timeEntries: [],
          expenseEntries: [],
        });
      }
      projectMap.get(entry.project_id).timeEntries.push(entry);
    });
    
    expenseEntries.forEach((entry) => {
      if (!projectMap.has(entry.project_id)) {
        projectMap.set(entry.project_id, {
          id: entry.project_id,
          name: entry.project_name,
          client_name: entry.client_name,
          timeEntries: [],
          expenseEntries: [],
        });
      }
      projectMap.get(entry.project_id).expenseEntries.push(entry);
    });
    
    return Array.from(projectMap.values());
  };

  const projectGroups = getProjectGroups();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-48 bg-[#E7E5E4] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('timeAndExpenses')}</h1>
          <p className="text-[#78716C] mt-1">{t('trackTimeAndExpenses')}</p>
        </div>
        <div className="flex gap-3">
          {/* Time Entry Dialog */}
          <Dialog open={timeDialogOpen} onOpenChange={setTimeDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="rounded-full h-11 px-6"
                onClick={() => openTimeDialog()}
                data-testid="add-time-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                {t('addTimeEntry')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium">{t('addTimeEntry')}</DialogTitle>
                <DialogDescription className="sr-only">Time entry form</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleTimeSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{t('project')} *</Label>
                  <Select
                    value={timeForm.project_id}
                    onValueChange={(value) => setTimeForm({ ...timeForm, project_id: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="time-project-select">
                      <SelectValue placeholder={t('selectProject')} />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} ({project.client_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('date')}</Label>
                    <Input
                      type="date"
                      value={timeForm.date}
                      onChange={(e) => setTimeForm({ ...timeForm, date: e.target.value })}
                      className="h-11"
                      data-testid="time-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('hoursWorked')} *</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      value={timeForm.hours}
                      onChange={(e) => setTimeForm({ ...timeForm, hours: e.target.value })}
                      placeholder="0"
                      className="h-11"
                      data-testid="time-hours-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('notes')}</Label>
                  <Textarea
                    value={timeForm.notes}
                    onChange={(e) => setTimeForm({ ...timeForm, notes: e.target.value })}
                    placeholder={t('optionalNotes')}
                    rows={3}
                    data-testid="time-notes-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setTimeDialogOpen(false)} className="rounded-full">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90" disabled={saving} data-testid="save-time-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Expense Entry Dialog */}
          <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6"
                onClick={() => openExpenseDialog()}
                data-testid="add-expense-btn"
              >
                <Receipt className="w-4 h-4 mr-2" />
                {t('addExpenseEntry')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium">{t('addExpenseEntry')}</DialogTitle>
                <DialogDescription className="sr-only">Expense entry form</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleExpenseSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{t('project')} *</Label>
                  <Select
                    value={expenseForm.project_id}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, project_id: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="expense-project-select">
                      <SelectValue placeholder={t('selectProject')} />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} ({project.client_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('expenseType')} *</Label>
                  <Select
                    value={expenseForm.expense_type}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, expense_type: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="expense-type-select">
                      <SelectValue placeholder={t('selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('cost')} *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]">{getCurrencySymbol()}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={expenseForm.cost}
                        onChange={(e) => setExpenseForm({ ...expenseForm, cost: e.target.value })}
                        placeholder="0.00"
                        className="h-11 pl-8"
                        data-testid="expense-cost-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('date')}</Label>
                    <Input
                      type="date"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                      className="h-11"
                      data-testid="expense-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('notes')}</Label>
                  <Textarea
                    value={expenseForm.notes}
                    onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                    placeholder={t('optionalNotes')}
                    rows={3}
                    data-testid="expense-notes-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)} className="rounded-full">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90" disabled={saving} data-testid="save-expense-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Project Groups */}
      {projectGroups.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
            <p className="text-[#78716C]">{t('noTimeEntries')}</p>
            <p className="text-sm text-[#A8A29E] mt-1">{t('trackTimeAndExpenses')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {projectGroups.map((group) => {
            const totalHours = group.timeEntries.reduce((sum, e) => sum + e.hours, 0);
            const totalCost = group.expenseEntries.reduce((sum, e) => sum + e.cost, 0);
            
            return (
              <Card key={group.id} className="border-0 shadow-sm" data-testid={`project-group-${group.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#78866B]/10 flex items-center justify-center">
                        <FolderKanban className="w-5 h-5 text-[#78866B]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-medium">{group.name}</CardTitle>
                        <p className="text-sm text-[#78716C]">{group.client_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {totalHours > 0 && (
                        <span className="text-[#78866B] font-medium flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" /> {totalHours.toFixed(1)}h
                        </span>
                      )}
                      {totalCost > 0 && (
                        <span className="text-[#C98E75] font-medium flex items-center gap-1">
                          <Receipt className="w-3.5 h-3.5" /> {formatAmount(totalCost)}
                        </span>
                      )}
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs text-[#78866B] hover:text-[#78866B] hover:bg-[#78866B]/10"
                          onClick={() => openTimeDialog(group.id)}
                          data-testid={`add-time-to-${group.id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          <Clock className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs text-[#C98E75] hover:text-[#C98E75] hover:bg-[#C98E75]/10"
                          onClick={() => openExpenseDialog(group.id)}
                          data-testid={`add-expense-to-${group.id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          <Receipt className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1">
                    {/* Time entries */}
                    {group.timeEntries.map((entry) => (
                      <div
                        key={`t-${entry.id}`}
                        className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-[#F5F5F4] transition-colors group"
                        data-testid={`time-entry-${entry.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#78866B]/10 flex items-center justify-center">
                            <Clock className="w-3.5 h-3.5 text-[#78866B]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#292524]">
                              {format(new Date(entry.date), 'MMM d')}
                              {entry.notes && <span className="text-[#78716C]"> — {entry.notes}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-[#78866B]">{entry.hours}h</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteTime(entry)}
                            data-testid={`delete-time-${entry.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {/* Expense entries */}
                    {group.expenseEntries.map((entry) => (
                      <div
                        key={`e-${entry.id}`}
                        className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-[#F5F5F4] transition-colors group"
                        data-testid={`expense-entry-${entry.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#C98E75]/10 flex items-center justify-center">
                            <Receipt className="w-3.5 h-3.5 text-[#C98E75]" />
                          </div>
                          <div>
                            <p className="text-sm text-[#292524]">
                              {format(new Date(entry.date), 'MMM d')}
                              <span className="text-xs text-[#78716C] bg-[#F5F5F4] px-1.5 py-0.5 rounded-full ml-2">
                                {EXPENSE_TYPES.find(t => t.value === entry.expense_type)?.label || entry.expense_type}
                              </span>
                              {entry.notes && <span className="text-[#78716C]"> — {entry.notes}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-[#C98E75]">{formatAmount(entry.cost)}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteExpense(entry)}
                            data-testid={`delete-expense-${entry.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
